import {
    REPORT_FOOTER_MEASUREMENT_ID,
    REPORT_HEADER_MEASUREMENT_ID,
    REPORT_PATIENT_INFO_MEASUREMENT_ID,
    REPORT_SIGNATURE_MEASUREMENT_ID,
} from '@/components/preview/pagination/constants';
import { SECTIONS } from '@/components/wizard/constants';
import { A4_PAGE_WIDTH } from '@/constants';
import { useReportSections } from '@/hooks/use-report-sections';
import { MeasureWrapper } from '../MeasureWrapper';
import PreviewPsychologistSignature from '../PreviewPsychologistSignature';
import { ReportFooter } from '../ReportFooter';
import { ReportFootnoteMeasurer } from '../ReportFootnotes';
import { ReportHeader } from '../ReportHeader';
import StandalonePatientInfoTable from './StandalonePatientInfoTable';
import StandaloneReportSection from './StandaloneReportSection';
import StandaloneSectionTitle from './StandaloneSectionTitle';
import StandaloneTestsRenderer from './StandaloneTestsRenderer';
import type { ReportFootnotePlan } from '../footnotes';
import type { Psychologist, ReportData } from '../types';

interface StandaloneHiddenReportMeasurerProps {
    data: ReportData;
    psychologist: Psychologist | null;
    footnotePlan: ReportFootnotePlan;
}

export default function StandaloneHiddenReportMeasurer({
    data,
    psychologist,
    footnotePlan,
}: StandaloneHiddenReportMeasurerProps) {
    const sections = useReportSections(data);
    const footerMeasureStyle = {
        marginLeft: '-3rem',
        marginRight: '-3rem',
        width: A4_PAGE_WIDTH,
    };

    return (
        <div
            aria-hidden="true"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: A4_PAGE_WIDTH,
                visibility: 'hidden',
                pointerEvents: 'none',
                zIndex: -1000,
                padding: '3rem',
            }}
            className="report-measurement-container"
        >
            <MeasureWrapper id={REPORT_HEADER_MEASUREMENT_ID}>
                <ReportHeader data={data} psychologist={psychologist} />
            </MeasureWrapper>

            <MeasureWrapper id={REPORT_PATIENT_INFO_MEASUREMENT_ID}>
                <StandalonePatientInfoTable data={data} psychologist={psychologist} />
            </MeasureWrapper>

            <MeasureWrapper id={REPORT_FOOTER_MEASUREMENT_ID} style={footerMeasureStyle}>
                <ReportFooter
                    data={data}
                    currentPage={1}
                    totalPages={2}
                    psychologist={psychologist}
                />
            </MeasureWrapper>

            <ReportFootnoteMeasurer footnotePlan={footnotePlan} />

            {sections.map((section) => {
                if (section.id === SECTIONS.TESTS) {
                    return (
                        <div key={section.id} className="mb-8">
                            <MeasureWrapper id={`section-${section.id}-title`}>
                                <StandaloneSectionTitle section={section} data={data} />
                            </MeasureWrapper>

                            <StandaloneTestsRenderer
                                data={data}
                                blocks={data.tests?.blocks}
                                measureBlocks={true}
                                footnotePlan={footnotePlan}
                            />
                        </div>
                    );
                }

                return (
                    <div key={section.id} className="mb-8">
                        <MeasureWrapper id={`section-${section.id}-title`}>
                            <StandaloneSectionTitle section={section} data={data} />
                        </MeasureWrapper>

                        <MeasureWrapper id={`section-${section.id}`}>
                            <StandaloneReportSection
                                section={{ ...section, key: section.id }}
                                data={data}
                                footnotePlan={footnotePlan}
                            />
                        </MeasureWrapper>
                    </div>
                );
            })}

            <MeasureWrapper id={REPORT_SIGNATURE_MEASUREMENT_ID}>
                <PreviewPsychologistSignature
                    full_name={psychologist?.full_name}
                    crp={psychologist?.crp}
                    city={psychologist?.city}
                    state={psychologist?.state}
                />
            </MeasureWrapper>
        </div>
    );
}
